// src/components/MainLayout.tsx

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Header from './Header';
import DictionarySidebar from './DictionarySidebar';
import InspirationalQuote from './InspirationalQuote';
import Footer from './Footer';
import { useLocation } from 'react-router-dom';
import DynamicBackground from './DynamicBackground';

const MIN_SIDEBAR_WIDTH = 330;
const MAX_SIDEBAR_WIDTH = 800;

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem('sidebarWidth');
    return savedWidth ? parseInt(savedWidth, 10) : 350;
  });

  // Dùng isDragging để quản lý trạng thái đang kéo
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Logic tính toán mới: Chiều rộng mới chính là vị trí X của con trỏ chuột
    const newWidth = e.clientX;

    // Áp dụng giới hạn min/max
    if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= MAX_SIDEBAR_WIDTH) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Lưu giá trị cuối cùng vào localStorage
    localStorage.setItem('sidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // useEffect để thêm/gỡ event listeners cho toàn bộ cửa sổ
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    // Cleanup function để gỡ event listeners
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="main-layout">
      <DynamicBackground />
      <Header />
      <div className="layout-container-with-sidebar">
        <aside
          ref={sidebarRef}
          className="dictionary-sidebar"
          style={{ width: `${sidebarWidth}px` }}
        >
          <DictionarySidebar />
        </aside>

        <div
          className={`resizer ${isDragging ? 'is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
        />

        <main className="main-content">
          {location.pathname === '/' && <InspirationalQuote />}
          {children}
        </main>
      </div>
      <Footer />
    </div>
  );
}